import {useFormikContext} from 'formik'
import {ServiceRequestStep} from '../../../genericComponents/formTemplate/steps/ServiceRequestStep'
import {Description} from '../../../../../genericComponents/typographie/Description'
import {WorkflowSelector} from '../../../model/WorkflowSelector'
import {ServiceEnum} from '../../../../model/ServiceEnum'
import {Label} from '../../../../../genericComponents/typographie/Label'
import {FileDocument} from '../../../../../displaySet/model/AttributeValue'
import React, {ReactElement} from 'react'
import {ItemCreationForm} from '../../../../model/NewItemInput'
import {Link} from '@mui/material'
import * as FileSaver from 'file-saver'
import {ServiceAttribute} from '../../../rtkServiceAPI'

export type FormFieldValue = string | boolean | FileDocument[]

const FieldSummary = ({label, value}: {
    label: string,
    value?: string
}) => {
    return (
        <>
            <Label className="form-summary-label">{label}</Label>
            <span className="form-summary-value">{value ?? '-'}</span>
        </>
    )
}

const StringDisplay = ({value}: { value: string | undefined }) => <span
    className="form-summary-value">{value ?? '-'}</span>
const FileDisplay = ({value}: { value: FileDocument }) => {
    const getFile = () => {
        const file = value.localFile
        if (!file) return
        FileSaver.saveAs(file, file.name)
    }
    if (value.file) {
        return <div className="form-summary-value-file">
            <span className="form-summary-value">File</span>
            <Link className="form-summary-value link" onClick={getFile}>
                {value.file.name ?? '-'}
            </Link>
        </div>
    }
    if (value.externalUrl) {
        return <div className="form-summary-value-file">
            <span className="form-summary-value">URL</span>
            <Link className="form-summary-value link" href={value.externalUrl}
                  target="_blank">
                {value.externalUrl}
            </Link>
        </div>
    }
    return <StringDisplay value={undefined}/>
}

const getFieldDisplay = (value: FormFieldValue): ReactElement => {
    if (typeof value === 'string') {
        return <StringDisplay value={value}/>
    }
    if (typeof value === 'boolean') {
        const displayValue = value ? 'Yes' : 'No'
        return <StringDisplay value={displayValue}/>
    }
    if (Array.isArray(value) && value.length > 0) {
        return (
            <>
                {value.map((fileDoc, index) => (
                    <FileDisplay key={index} value={fileDoc}/>
                ))}
            </>
        )
    }
    return <></>
}

const AttributeFieldSummary = ({label, values}: {
    label: string,
    values: FormFieldValue[]
}) => {
    // Remove empty attributes if at least one contain a value
    if (values.length > 1) {
        values = values.filter(value => value !== undefined)
    }
    const getFieldValue = (value: FormFieldValue): ReactElement => {
        if (value === undefined || value === null) return <StringDisplay value={value}/>
        return getFieldDisplay(value)
    }

    return (
        <>
            <Label className="form-summary-label">{label}</Label>
            <div className="form-summary-values">
                {values && values.map(value => getFieldValue(value))}
            </div>
        </>
    )
}

const extractComposedAttributes = (attribute: ServiceAttribute, acc: Map<string, ServiceAttribute[]>): Map<string, ServiceAttribute[]> => {
    const composedByAttributes = attribute.attribute.value.composedByAttributes
    composedByAttributes.forEach((attributeModel) => {
        const serviceAttribute: ServiceAttribute = {
            isRequired: false,
            attribute: attributeModel
        }
        acc.set(attributeModel.name, [serviceAttribute])
    })
    return acc
}

export const ReviewNewItemsForm = ({serviceType}: {
    serviceType: ServiceEnum,
}) => {
    const formik = useFormikContext<ItemCreationForm>()

    return (
        <ServiceRequestStep>
            <Description>Please <b>review</b> your selections and submit.</Description>

            <div data-testid="manufacturer-item-creation-summary" className="service-request-form-summary">
                <FieldSummary label="Part Number" value={formik?.values?.partNumber}/>
                <FieldSummary label="Defined by" value={formik?.values?.definedByOrganization?.label}/>
                <FieldSummary label="Technical Classification"
                              value={formik?.values?.technicalClassificationNode?.label}/>
            </div>
            <WorkflowSelector service={serviceType}/>
        </ServiceRequestStep>
    )
}
